import type { AudioBus, AudioSettings } from '../../audio/types';
import type { DisplaySettings } from './displaySettings';

/**
 * What "unsaved changes" means, with no dependencies.
 *
 * The settings screen previews every change the moment it is made — a volume
 * you cannot hear is a volume you cannot choose — so "in effect" and "saved"
 * stop being the same thing while that screen is open. The difference between
 * the two is what enables the save button and what raises the exit prompt, and
 * it is decided here so it can be reasoned about without an audio graph, a
 * router or a DOM in the room.
 */

export interface SettingsSnapshot {
  display: DisplaySettings;
  audio: AudioSettings;
  hapticsEnabled: boolean;
}

const BUSES: readonly AudioBus[] = ['master', 'music', 'sfx'];

/**
 * A snapshot has to survive the values it was taken from being mutated: it is
 * kept precisely so it can be compared against — and restored over — a live
 * state that keeps moving under it.
 */
export function cloneSettingsSnapshot(snapshot: SettingsSnapshot): SettingsSnapshot {
  return {
    display: { ...snapshot.display },
    audio: {
      master: { ...snapshot.audio.master },
      music: { ...snapshot.audio.music },
      sfx: { ...snapshot.audio.sfx },
    },
    hapticsEnabled: snapshot.hapticsEnabled,
  };
}

/**
 * Field by field rather than by serialising both sides: a key added to any of
 * these three shapes has to be answered for here, and a compiler error is a
 * better way to find that out than a save button that quietly stops lighting up.
 */
export function settingsSnapshotsEqual(a: SettingsSnapshot, b: SettingsSnapshot): boolean {
  return (
    a.display.crt === b.display.crt &&
    a.display.reducedMotion === b.display.reducedMotion &&
    a.display.language === b.display.language &&
    a.hapticsEnabled === b.hapticsEnabled &&
    BUSES.every(
      (bus) =>
        a.audio[bus].volume === b.audio[bus].volume && a.audio[bus].muted === b.audio[bus].muted,
    )
  );
}
